import type { JSX } from 'react';

import { REVEAL_STAGES, stageIndex, stageStepLabel } from '../../lib/revealStaging';
import type { RevealStage } from '../../lib/revealStaging';

// The five-stage progress rail. A stage at or below the data ceiling is
// reachable (the user can jump back to a settled stage); stages ahead of the
// ceiling are shown but not yet navigable — the reveal "fills later stages in as
// their results land" (design D7) rather than gating behind a single spinner.

export interface RevealStepperProps {
  current: RevealStage;
  ceiling: RevealStage;
  onJump: (stage: RevealStage) => void;
}

export function RevealStepper({ current, ceiling, onJump }: RevealStepperProps): JSX.Element {
  const currentIdx = stageIndex(current);
  const ceilingIdx = stageIndex(ceiling);

  return (
    <ol className="reveal-stepper" aria-label="Setup progress">
      {REVEAL_STAGES.map((stage) => {
        const idx = stageIndex(stage);
        const isCurrent = stage === current;
        const isDone = idx < currentIdx;
        const isReachable = idx <= Math.max(ceilingIdx, currentIdx);
        const cls = [
          'reveal-stepper__step',
          isCurrent ? 'is-current' : '',
          isDone ? 'is-done' : '',
          isReachable ? 'is-reachable' : '',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <li className={cls} key={stage}>
            <button
              type="button"
              className="reveal-stepper__btn"
              disabled={!isReachable || isCurrent}
              aria-current={isCurrent ? 'step' : undefined}
              onClick={() => onJump(stage)}
            >
              <span className="reveal-stepper__marker" aria-hidden="true">
                {isDone ? '✓' : idx + 1}
              </span>
              <span className="reveal-stepper__label">{stageStepLabel(stage)}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
